/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React from 'react';
import { View } from '@az/starc-ui';
import { resolveColor } from '../CMSLiveTextHero/utils';
import Carousel from './Carousel';
import SectionTitle from './SectionTitle';
import FeaturedCta from './FeaturedCta';
import styles from './FeatureCarousel.module.scss';

type ContentStackImage = {
  image_url?: string;
  image_alt_text?: string;
  attributes?: unknown[];
};

type FeatureCarouselOptions = {
  title: string;
  component_id?: string;
  heading: string;
  link?: { title: string; href: string };
  isbackground_color?: boolean;
  background_color?: unknown;
  background_image?: ContentStackImage;
  feature_carousel_content: Array<{
    heading: string;
    sub_heading: string;
    text_color?: unknown;
    image?: ContentStackImage;
    link: string;
    _metadata?: { uid?: string };
  }>;
};

export type FeatureCarouselProps = {
  options: FeatureCarouselOptions;
};
const FeatureCarousel = ({ options }: FeatureCarouselProps) => {
  const backgroundImage = options.background_image?.image_url;
  const background =
    !options.isbackground_color && backgroundImage
      ? { type: 'image' as const, src: backgroundImage }
      : { type: 'color' as const, value: resolveColor(options.background_color) ?? '#F2F2F2' };
  const cards = options.feature_carousel_content.map((content, index) => {
    const imageUrl = content.image?.image_url;

    return {
      id: content._metadata?.uid ?? `${content.heading}-${index}`,
      title: content.heading,
      subtitle: content.sub_heading,
      image: imageUrl
        ? {
            src: imageUrl,
            alt: content.image?.image_alt_text ?? '',
            width: 156,
            height: 156,
          }
        : undefined,
      cta: { href: content.link },
      background,
      textColor: resolveColor(content.text_color),
    };
  });

  return (
    <View className={styles.FeatureCarousel}>
      <SectionTitle
        title={options.heading}
        ctaText={options.link?.title}
        ctaLink={options.link?.href}
      />
      <View className={styles.GapSection} />
      <Carousel cards={cards} />
      <View className={styles.MobileCtaSection}>
        <FeaturedCta text={options.link?.title} link={options.link?.href} compact />
      </View>
    </View>
  );
};

export default FeatureCarousel;
